import {
  AGENT_LIMITS, AgentAction, AgentActivity, AgentApi, AgentChangeset, AgentContext, AgentConversation, AgentCreateBody, AgentErrorContext, AgentEvent,
  AgentFile, AgentFollowSignal, AgentMessage, AgentRun, AgentRunStatus, AgentScope, AgentSnapshotResponse, AgentStep, HistoryStatus, PHASE_LABELS, RawRecord,
  actionErrorMessage, activityPhase, byDate, errorCode, fileErrorMessage, friendlyError, historyStatus, isActiveRun, isFatalError, isFinalRun, isReadingFile,
  lastActionLabel, mergeAction, mergeChangeset, mergeFile, normalizeAction, normalizeChangeset, normalizeConversation, normalizeEvent, normalizeFile,
  normalizeMessage, normalizeQuestion, normalizeRun, normalizeRunState, normalizeTarget, runStatusLabel, stepLabel, uploadProblem, upsert
} from './agent-ia.models';

// Panel state for the agent v2 API (SPEC §4, §8). No Angular dependency: tested with node:test.
//
// Consistency rules:
// - one read (snapshot or events) at a time per conversation, identified by its AbortController;
//   a read whose controller is no longer current is ignored, whatever its transport does;
// - writes that change the run (send, answer, cancel) pause reads, so an older read can never
//   land after them; other writes (upload, approve/reject, undo) only touch entities merged
//   monotonically (a late value never moves a file, action or changeset backwards);
// - events are applied once, in seq order (after = lastSeq); run status events older than the
//   last snapshot or run write (runSeq) are ignored;
// - switching conversation or disposing bumps `epoch`: late answers from the previous
//   conversation are dropped. Closing the panel stops reading, except while the conversation is watched (follow mode:
//   run active or stopping, approved action being executed, or run resumed in the background after a reload);
// - follow mode: every event above followFloor (set by the first snapshot of a conversation, never raised by later
//   snapshots) is handed once to onFollow with the run state at that event; replayed history never is.

export interface AgentTimers { setTimeout(callback: () => void, ms: number): unknown; clearTimeout(handle: unknown): void; }
export interface AgentStorage { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void; }
export interface AgentStateOptions {
  requestId?: () => string; timers?: AgentTimers; now?: () => number;
  storage?: AgentStorage | null;                  // sessionStorage of the tab: run to resume after a reload (follow mode)
  onFollow?: (signal: AgentFollowSignal) => void; // fresh events, for the follow engine
  onReset?: () => void;                           // conversation left (select, new, dispose): pending follow intents are dropped
  flush?: () => Promise<boolean>;                 // registered pages save their pending edits before send / answer / approve / undo
}
interface Draft { text: string; fileIds: string[]; answer: string; answerOptions: string[]; questionKey: string; }

export const POLL = Object.freeze({
  fastMs: 1000, fastMaxMs: 5000,     // approved action being executed
  activeMaxMs: 2000,                 // run queued/running: at most 2 s between reads (follow mode)
  slowMs: 2500, slowMaxMs: 10000,    // files being read
  errorMs: 2000, errorMaxMs: 30000,  // read failures (doubling)
  growth: 1.5,                       // slow down after each poll without new events
  eventsLimit: 200,
  replay: 50                         // events re-read when opening a conversation with an active run (progress)
});

const emptyDraft = (): Draft => ({ text: '', fileIds: [], answer: '', answerOptions: [], questionKey: '' });
// Follow mode: run started (or adopted) in this tab; a waiting run is resumed after a reload for 30 min.
const SESSION_KEY = 'aglo.agentIa.followedRun';
const RESUME_WAITING_MS = 30 * 60 * 1000;
export const FLUSH_ERROR = 'Vos modifications en cours n’ont pas pu être enregistrées. Enregistrez-les dans la page, puis réessayez.';
const RUN_EVENT_STATUS: Record<string, AgentRunStatus> = {
  run_queued: 'queued', run_started: 'running', question: 'waiting_user', run_completed: 'completed', run_failed: 'failed', run_cancelled: 'cancelled'
};
const SESSION_ID = /^[\w-]{1,200}$/;
const finalStatus = (status: AgentRunStatus) => status === 'completed' || status === 'failed' || status === 'cancelled';
const defaultStorage = (): AgentStorage | null => { try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; } };
const defaultTimers: AgentTimers = { setTimeout: (callback, ms) => setTimeout(callback, ms), clearTimeout: handle => clearTimeout(handle as any) };
// getRandomValues is also available on HTTP when the local app is accessed over Tailscale.
const defaultRequestId = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
const text = (value: unknown, max: number) => typeof value === 'string' ? value.slice(0, max) : value == null ? '' : String(value).slice(0, max);
const newestFirst = (list: AgentChangeset[]) => byDate(list).reverse();

export class AgentIaState {
  opened = false;
  view: 'conversation' | 'history' = 'conversation';
  conversations: AgentConversation[] = [];
  conversation: AgentConversation | null = null;
  messages: AgentMessage[] = [];
  run: AgentRun | null = null;
  steps: AgentStep[] = [];
  actions: AgentAction[] = [];
  changesets: AgentChangeset[] = [];
  files: AgentFile[] = [];
  lastSeq = 0;
  loading = false;
  audioBusy = false;
  listing = false;
  sending = false;
  uploading = false;
  cancelling = false;
  deciding = new Set<string>();
  undoing = new Set<string>();
  error = '';
  draft: Draft = emptyDraft();

  private drafts = new Map<string, Draft>();
  private epoch = 0;
  private listEpoch = 0;
  private listRead: AbortController | null = null;
  private read: AbortController | null = null;
  private timer: unknown = null;
  private pausing = new Set<object>();
  private creating: Promise<AgentConversation> | null = null;
  private loaded = false;
  private needsSnapshot = false;
  private decidedElsewhere: string[] = [];
  private pollSoon = false;
  private snapshotSeq = 0;
  private runSeq = 0;
  private idlePolls = 0;
  private failures = 0;
  private stopped = false;
  private readError = false;
  private retry: { signature: string; requestId: string } | null = null;
  private disposed = false;
  private watching = false;
  private followFloor = 0;
  private followRun: { id: string; status: AgentRunStatus } | null = null;
  private liveRun = '';
  private lastAction: { runId: string; text: string } | null = null;
  private activityMemo: { key: string; value: AgentActivity } | null = null;
  private session: { conversationId: string; runId: string } | null | undefined;
  private readonly requestId: () => string;
  private readonly timers: AgentTimers;
  private readonly now: () => number;
  private readonly storage: AgentStorage | null;
  private readonly onFollow?: (signal: AgentFollowSignal) => void;
  private readonly onReset?: () => void;
  private readonly flush?: () => Promise<boolean>;

  constructor(private api: AgentApi, private changed: () => void, options: AgentStateOptions = {}) {
    this.requestId = options.requestId || defaultRequestId;
    this.timers = options.timers || defaultTimers;
    this.now = options.now || (() => Date.now());
    this.storage = options.storage === undefined ? defaultStorage() : options.storage;
    this.onFollow = options.onFollow; this.onReset = options.onReset; this.flush = options.flush;
  }

  get runActive() { return isActiveRun(this.run); }
  get waiting() { return this.run?.status === 'waiting_user'; }
  get stopping() { return this.cancelling || Boolean(this.run?.cancelRequested && !isFinalRun(this.run)); }
  get canCancel() { return Boolean(this.run && (this.runActive || this.waiting) && !this.stopping && !this.sending); }
  get statusLabel() { return runStatusLabel(this.run); }
  get draftFiles() { return this.draft.fileIds.map(id => this.files.find(file => file.id === id)).filter((file): file is AgentFile => Boolean(file)); }
  get readingDraftFiles() { return this.draftFiles.some(isReadingFile); }
  get pendingActions() { return this.actions.filter(action => action.status === 'pending'); }
  get decidedActions() { return this.actions.filter(action => action.status !== 'pending'); }
  // Journal of an AI connection (source 'mcp', docs/mcp/06-FRONT.md §7): the API refuses messages (409), nothing is sent
  // nor attached there; its changes can still be undone.
  get readOnly() { return this.conversation?.source === 'mcp'; }
  get canSend() {
    const content = this.draft.text.trim();
    return Boolean(content) && content.length <= AGENT_LIMITS.messageChars && !this.sending && !this.uploading && !this.loading && !this.audioBusy
      && !this.runActive && !this.waiting && !this.readingDraftFiles && !this.readOnly;
  }
  get canUpload() { return !this.uploading && !this.sending && !this.loading && !this.audioBusy && !this.readOnly; }
  // Reading continues with the panel closed while this is true (follow mode).
  private get busy() { return this.runActive || this.stopping || this.actions.some(action => action.status === 'approved' || action.status === 'executing'); }
  private get reading() { return this.opened || this.watching; }
  // The current run was sent (or adopted with « Suivre ici ») from this tab: only that tab follows it.
  get ownsRun() {
    const saved = this.saved(), run = this.run;
    return Boolean(saved && run && saved.runId === run.id && saved.conversationId === this.conversation?.id);
  }
  // Activity bubble (follow mode): phase of the run, « now » text, last change of the run, start of the current phase.
  // A final state is only shown for a run seen active here (opening an old conversation shows nothing).
  get activity(): AgentActivity | null {
    const run = this.run, phase = activityPhase(run, this.steps, this.pendingActions.length, this.stopping);
    const live = Boolean(run && run.id === this.liveRun);
    if (!phase || (['done', 'failed', 'cancelled'].includes(phase) && !live)) return null;
    const step = phase === 'working' ? this.steps[this.steps.length - 1] : null;
    const last = run && this.lastAction?.runId === run.id ? this.lastAction.text : '';
    // Panel closed and reading stopped on a fatal error (expired session…): the run state shown would be stale.
    const interrupted = this.stopped && this.readError && !this.opened && !['done', 'failed', 'cancelled'].includes(phase);
    const key = [run?.id || '', phase, step?.seq ?? '', interrupted].join('|');
    const now = interrupted ? 'Suivi interrompu : ouvrez la conversation' : step?.label || PHASE_LABELS[phase];
    if (this.activityMemo?.key !== key) this.activityMemo = { key, value: { runId: run?.id || '', phase, now, last, since: this.now(), live } };
    else if (this.activityMemo.value.last !== last || this.activityMemo.value.live !== live) this.activityMemo.value = { ...this.activityMemo.value, last, live };
    return this.activityMemo.value;
  }
  get answerContent() {
    const question = this.run?.question;
    const choices = question?.options.filter(option => this.draft.answerOptions.includes(option)) || [];
    const selected = question?.multiple ? choices.length ? `Choix retenus :\n${choices.map(option => `- ${option}`).join('\n')}` : '' : choices[0] || '';
    const comment = this.draft.answer.trim();
    return selected && comment ? `${selected}\n\nPrécisions : ${comment}` : selected || comment;
  }
  get answerTooLong() { return this.answerContent.length > AGENT_LIMITS.messageChars; }
  get canAnswer() { return Boolean(this.waiting && this.run?.question && this.conversation && this.answerContent && !this.answerTooLong && !this.sending && !this.stopping); }
  selectAnswerOption(option: string) {
    const question = this.run?.question;
    if (!this.waiting || !question?.options.includes(option) || this.sending || this.stopping) return;
    this.syncQuestionDraft();
    this.draft.answerOptions = question.multiple
      ? this.draft.answerOptions.includes(option) ? this.draft.answerOptions.filter(value => value !== option) : [...this.draft.answerOptions, option]
      : [option];
    this.changed();
  }
  // Final summary of a completed run whose assistant message is not loaded (yet).
  get summaryOnly() {
    const run = this.run;
    if (run?.status !== 'completed' || !run.summary) return '';
    return this.messages.some(message => message.role === 'assistant' && (message.runId === run.id || message.content === run.summary)) ? '' : run.summary;
  }
  // History list: live run of the open conversation, otherwise the last run sent by GET /conversations.
  historyStatus(item: AgentConversation): HistoryStatus | null {
    return historyStatus(item.id === this.conversation?.id && this.loaded ? this.run : item.lastRun);
  }
  messageFiles(message: AgentMessage) { return message.fileIds.map(id => this.files.find(file => file.id === id)).filter((file): file is AgentFile => Boolean(file)); }
  // Conversation files neither attached to a message nor waiting in the draft.
  get otherFiles() {
    const shown = new Set([...this.messages.flatMap(message => message.fileIds), ...this.draft.fileIds]);
    return this.files.filter(file => !shown.has(file.id));
  }

  // ---- Panel and navigation -------------------------------------------------------------

  open() {
    if (this.disposed || this.opened) return;
    this.opened = true; this.changed();
    this.loadHistory();
    if (this.conversation) this.refresh();
  }
  // A closed panel keeps reading a busy conversation (follow mode); otherwise it stops.
  close() {
    this.opened = false; this.listRead?.abort(); this.listRead = null; this.listing = false;
    this.watching = !this.disposed && Boolean(this.conversation) && (this.watching || this.busy);
    if (!this.watching) this.stopRead();
    this.changed();
  }
  dispose() {
    this.disposed = true; this.watching = false; this.close(); this.reset(); this.drafts.clear();
    this.conversation = null; this.conversations = []; this.draft = emptyDraft();
  }
  newConversation() {
    this.forget(); this.remember(); this.reset(); this.conversation = null;
    this.draft = this.drafts.get('new') || emptyDraft(); this.view = 'conversation'; this.changed();
  }
  select(id: string, background = false) {
    if (!id || this.disposed) return Promise.resolve();
    if (this.saved()?.conversationId !== id) this.forget();
    this.remember(); this.reset(); this.watching = background;
    this.conversation = this.conversations.find(item => item.id === id) || { id, title: '', scope: {}, createdAt: '', updatedAt: '' };
    this.draft = this.drafts.get(id) || emptyDraft(); this.view = 'conversation'; this.changed();
    return this.poll();
  }
  // Mount of the panel: resume in the background the run this tab started before a reload (sessionStorage), if still
  // queued / running, or waiting for an answer since less than 30 min. Other tabs never resume it.
  async resume() {
    const saved = this.saved();
    if (!saved || this.disposed || this.conversation) return;
    await this.select(saved.conversationId, true);
    const conversation = this.conversation, run = this.run;
    // Not read yet (the panel was opened meanwhile and restarted the read): keep the saved run, decide on a later mount.
    if (this.disposed || conversation?.id !== saved.conversationId || !this.loaded) return;
    const recent = this.now() - Date.parse(conversation.updatedAt || '') < RESUME_WAITING_MS;
    if (run?.id === saved.runId && (isActiveRun(run) || (run.status === 'waiting_user' && recent))) return;
    this.forget();
    if (this.opened) return;
    this.watching = false; this.reset(); this.conversation = null; this.draft = this.drafts.get('new') || emptyDraft(); this.changed();
  }
  // « Suivre ici »: this tab follows the current run (another tab or device started it).
  adoptRun() {
    if (!this.conversation || !this.run || isFinalRun(this.run)) return;
    this.own(this.conversation.id, this.run.id); this.changed();
  }
  // The tab is visible again: read now rather than at the next timer (follow mode).
  wake() {
    if (this.timer === null || this.read || !this.reading || this.disposed) return;
    this.clearTimer(); this.poll();
  }
  // Explicit refresh (button, reopening): full snapshot then events from lastSeq.
  refresh() {
    if (!this.conversation || this.disposed) return Promise.resolve();
    this.stopped = false; this.failures = 0; this.needsSnapshot = true; this.stopRead();
    return this.poll();
  }
  async loadHistory() {
    if (!this.opened || this.disposed) return;
    const epoch = ++this.listEpoch, controller = new AbortController();
    this.listRead?.abort(); this.listRead = controller; this.listing = true; this.changed();
    try {
      const result = await this.api.list(controller.signal);
      if (epoch !== this.listEpoch || !this.opened) return;
      const rows = (result?.conversations || []).map(normalizeConversation).filter(item => item.id);
      const local = this.conversation && !rows.some(item => item.id === this.conversation!.id) && this.loaded ? [this.conversation] : [];
      this.conversations = [...new Map([...local, ...rows].map(item => [item.id, item])).values()];
      if (this.readError) { this.error = ''; this.readError = false; }
    } catch (error) {
      if (epoch === this.listEpoch && this.opened) { this.error = friendlyError(error, 'list'); this.readError = true; }
    } finally {
      if (epoch === this.listEpoch) { this.listing = false; this.listRead = null; this.changed(); }
    }
  }

  // ---- Reading: one chain per conversation ----------------------------------------------

  async poll(): Promise<void> {
    const id = this.conversation?.id;
    if (!id || !this.reading || this.disposed || this.read || this.pausing.size) return;
    this.clearTimer(); this.pollSoon = false;
    const controller = new AbortController(); this.read = controller;
    const fresh = () => this.read === controller && !controller.signal.aborted;
    let fatal = false;
    try {
      if (!this.loaded || this.needsSnapshot) {
        if (!this.loaded) { this.loading = true; this.changed(); }
        const snapshot = await this.api.get(id, controller.signal);
        if (!fresh()) return;
        this.applySnapshot(snapshot);
      }
      const result = await this.api.events(id, this.lastSeq, POLL.eventsLimit, controller.signal);
      if (!fresh()) return;
      const events = Array.isArray(result?.events) ? result.events : [];
      const applied = this.applyEvents(events);
      this.dropDecided();
      this.idlePolls = applied ? 0 : this.idlePolls + 1;
      if (events.length >= POLL.eventsLimit) this.pollSoon = true;
      this.failures = 0;
      if (this.readError) { this.error = ''; this.readError = false; }
    } catch (error) {
      if (!fresh()) return;
      this.failures++; fatal = isFatalError(error);
      this.error = friendlyError(error, 'load'); this.readError = true;
    } finally {
      if (this.read === controller) {
        this.read = null; this.loading = false;
        if (fatal) this.stopped = true;
        // Watching ends with the first read that finds nothing left to follow (a final snapshot is still read).
        if (this.watching && !this.opened && (fatal || (!this.busy && !this.needsSnapshot && !this.pollSoon))) this.watching = false;
        this.schedule(); this.changed();
      }
    }
  }
  private pollDelay(): number | null {
    if (this.failures) return Math.min(POLL.errorMaxMs, POLL.errorMs * 2 ** (this.failures - 1));
    if (this.pollSoon || !this.loaded || this.needsSnapshot) return 0;
    const growth = POLL.growth ** Math.min(this.idlePolls, 10);
    if (this.runActive) return Math.min(POLL.activeMaxMs, Math.round(POLL.fastMs * growth));
    if (this.busy) return Math.min(POLL.fastMaxMs, Math.round(POLL.fastMs * growth));
    if (this.files.some(isReadingFile)) return Math.min(POLL.slowMaxMs, Math.round(POLL.slowMs * growth));
    return null;
  }
  private schedule() {
    this.clearTimer();
    if (!this.reading || this.disposed || !this.conversation || this.read || this.pausing.size || this.stopped) return;
    const wait = this.pollDelay();
    if (wait === null) return;
    this.timer = this.timers.setTimeout(() => { this.timer = null; this.poll(); }, wait);
  }
  private clearTimer() { if (this.timer !== null) this.timers.clearTimeout(this.timer); this.timer = null; }
  private stopRead() { this.clearTimer(); this.read?.abort(); this.read = null; this.loading = false; }

  private applySnapshot(raw: AgentSnapshotResponse) {
    const conversation = normalizeConversation(raw?.conversation || {});
    if (!conversation.id) throw new Error('Réponse invalide');
    const seq = Math.max(0, Number(raw.lastSeq) || 0), first = !this.loaded;
    this.conversation = conversation;
    this.conversations = this.conversations.map(item => item.id === conversation.id ? conversation : item);
    let messages = this.messages;
    for (const message of (raw.messages || []).map(normalizeMessage)) messages = upsert(messages, message);
    this.messages = byDate(messages);
    const run = raw.run ? normalizeRun(raw.run) : null;
    if (run?.id && run.id === this.run?.id) this.run = { ...run, lastText: this.run.lastText };
    else { this.run = run?.id ? run : null; this.steps = []; }
    if (run?.id && !isFinalRun(run)) this.liveRun = run.id;
    this.syncQuestionDraft();
    const pending = (raw.pendingActions || []).map(normalizeAction).filter(action => action.id);
    // A local pending action missing from the server list was decided meanwhile: its decision event
    // follows lastSeq and is applied by the same read; otherwise the action is dropped (dropDecided).
    this.decidedElsewhere = this.actions.filter(action => action.status === 'pending' && !pending.some(item => item.id === action.id)).map(action => action.id);
    let actions = this.actions;
    for (const action of pending) actions = upsert(actions, action, mergeAction);
    this.actions = actions;
    let changesets = this.changesets;
    for (const changeset of (raw.changesets || []).map(normalizeChangeset)) changesets = upsert(changesets, changeset, mergeChangeset);
    this.changesets = newestFirst(changesets);
    let files = this.files;
    for (const file of (raw.files || []).map(normalizeFile)) files = upsert(files, file, mergeFile);
    this.files = files;
    this.snapshotSeq = Math.max(this.snapshotSeq, seq); this.runSeq = seq; this.needsSnapshot = false; this.loaded = true;
    // First load: start just before the snapshot when a run is active, to show its recent progress. Only events after
    // this first snapshot are followed (a later snapshot taken mid-run must not hide the changesets it already covers).
    if (first) {
      this.lastSeq = isActiveRun(this.run) ? Math.max(0, seq - POLL.replay) : seq;
      this.followFloor = seq; this.followRun = this.run ? { id: this.run.id, status: this.run.status } : null;
    }
  }

  private dropDecided() {
    const ids = this.decidedElsewhere;
    this.decidedElsewhere = [];
    if (ids.length) this.actions = this.actions.filter(action => action.status !== 'pending' || !ids.includes(action.id));
  }

  private applyEvents(raws: RawRecord[]): number {
    const events = raws.map(normalizeEvent).filter((event): event is AgentEvent => Boolean(event)).sort((a, b) => a.seq - b.seq);
    let applied = 0;
    for (const event of events) {
      if (event.seq <= this.lastSeq) continue;
      const scope = this.conversation?.scope || {}, projectTitle = this.conversation?.projectTitle || '';
      this.applyEvent(event); this.lastSeq = event.seq; applied++;
      if (event.seq > this.followFloor) this.follow(event, scope, projectTitle);
    }
    return applied;
  }

  // Follow mode: the run is tracked in seq order (a snapshot read before these events may already show a later status).
  private follow(event: AgentEvent, scope: AgentScope, projectTitle: string) {
    const status = RUN_EVENT_STATUS[event.type], tracked = this.followRun;
    if (status && event.runId && (!tracked || tracked.id === event.runId || event.type === 'run_queued' || finalStatus(tracked.status))) {
      this.followRun = { id: event.runId, status };
      if (!finalStatus(status)) this.liveRun = event.runId;
    }
    if (!this.onFollow || !this.conversation) return;
    const knownKind = event.type === 'changeset_undone' ? this.changesets.find(change => change.id === text(event.data.changeSetId, 200))?.kind : undefined;
    try {
      this.onFollow({ event, run: this.followRun ? { ...this.followRun } : null, conversationId: this.conversation.id, scope: { ...scope }, projectTitle, owned: this.ownsRun, ...(knownKind ? { knownKind } : {}) });
    } catch { /* the follow engine never breaks the panel */ }
  }

  private applyEvent(event: AgentEvent) {
    const data = event.data, fresh = event.seq > this.snapshotSeq;
    switch (event.type) {
      case 'message_created':
        if (fresh && !this.messages.some(message => message.id === text(data.messageId, 200))) this.needsSnapshot = true;
        break;
      case 'run_queued': this.runEvent(event, { status: 'queued', cancelRequested: false, question: null, error: null }); break;
      case 'run_started': this.runEvent(event, { status: 'running', question: null }); break;
      case 'question':
        this.runEvent(event, { status: 'waiting_user', question: normalizeQuestion({ id: data.questionId, text: data.text, options: data.options, multiple: data.multiple }) });
        break;
      case 'run_completed': this.runEvent(event, { status: 'completed', summary: text(data.summary, 20000), question: null }, fresh); break;
      case 'run_failed':
        this.runEvent(event, { status: 'failed', question: null, error: { code: text(data.code, 60), message: text(data.message, 500) } }, fresh);
        break;
      case 'run_cancelled': this.runEvent(event, { status: 'cancelled', question: null }, fresh); break;
      case 'assistant_text':
        if (this.run && (!event.runId || event.runId === this.run.id)) this.run = { ...this.run, lastText: text(data.text, 4000) };
        break;
      case 'tool_started': case 'tool_finished': this.stepEvent(event); break;
      case 'changeset_applied': {
        const action = text(data.action, 30), kind = text(data.kind, 30), summary = text(data.summary, 500);
        this.addChangeset({ id: text(data.changeSetId, 200), kind, ...(action ? { action } : {}), summary, status: 'applied', createdAt: event.createdAt });
        this.noteAction(event, kind, summary);
        break;
      }
      case 'measurement_proposed': {
        const count = Number(data.count) || 0;
        const summary = `${count} métré${count > 1 ? 's' : ''} proposé${count > 1 ? 's' : ''} sur le plan, à vérifier`;
        this.addChangeset({ id: text(data.changeSetId, 200), kind: 'plan', summary, status: 'applied', createdAt: event.createdAt });
        this.noteAction(event, 'plan', summary);
        break;
      }
      case 'changeset_undone': this.updateChangeset(text(data.changeSetId, 200)); break;
      case 'conversation_scoped': {
        if (fresh && this.conversation) {
          this.conversation = normalizeConversation({ ...this.conversation, scope: data.scope || {}, projectTitle: data.projectTitle || '' });
          this.conversations = this.conversations.map(item => item.id === this.conversation!.id ? { ...item, scope: this.conversation!.scope, projectTitle: this.conversation!.projectTitle } : item);
        }
        break;
      }
      case 'action_proposed':
        this.actions = upsert(this.actions, {
          id: text(data.actionId, 200), type: text(data.type, 60), title: text(data.title, 200), description: text(data.description, 4000),
          targetLabel: '', status: 'pending', error: '', createdAt: event.createdAt
        }, mergeAction);
        if (fresh) this.needsSnapshot = true; // the target label (computed by the API) is only in the snapshot
        break;
      case 'action_executed': this.updateAction(text(data.actionId, 200), data.status === 'partially_executed' ? 'partially_executed' : 'executed'); break;
      case 'action_rejected': this.updateAction(text(data.actionId, 200), 'rejected'); break;
      // Proposals of phase 3 (back/docs/mcp/03-APPROBATION.md §4): executed by a job, they may expire or be withdrawn.
      case 'action_executing': this.updateAction(text(data.actionId, 200), 'executing'); break;
      case 'action_partially_executed': this.updateAction(text(data.actionId, 200), 'partially_executed'); break;
      case 'action_expired': this.updateAction(text(data.actionId, 200), 'expired'); break;
      case 'action_withdrawn': this.updateAction(text(data.actionId, 200), 'withdrawn'); break;
      case 'action_failed': this.updateAction(text(data.actionId, 200), 'failed', actionErrorMessage(data.code, data.message)); break;
      case 'file_ingested': this.updateFile(event, text(data.fileId, 200), 'ready', ''); break;
      case 'file_failed': this.updateFile(event, text(data.fileId, 200), 'failed', fileErrorMessage(data.code, data.message)); break;
    }
  }
  private runEvent(event: AgentEvent, patch: Partial<AgentRun>, refresh = false) {
    if (refresh) this.needsSnapshot = true; // final message, actions and changesets of the run
    if (event.seq <= this.runSeq || !event.runId) return;
    if (!this.run || this.run.id !== event.runId) {
      // A new run starts with run_queued; other events of an unknown run are ignored while the current one is not final.
      if (this.run && event.type !== 'run_queued' && !isFinalRun(this.run)) return;
      this.run = { id: event.runId, status: 'queued', cancelRequested: false, question: null, error: null, summary: '', lastText: '' };
      this.steps = [];
    }
    this.run = { ...this.run, ...patch };
    this.syncQuestionDraft();
  }
  private stepEvent(event: AgentEvent) {
    if (!this.run || (event.runId && event.runId !== this.run.id) || this.steps.some(step => step.seq === event.seq)) return;
    const tool = text(event.data.tool, 60);
    if (event.type === 'tool_started') {
      this.steps = [...this.steps, { seq: event.seq, tool, label: stepLabel({ tool, label: text(event.data.label, 200) }), status: 'running' as const, summary: '' }].slice(-30);
      return;
    }
    const finished = { status: event.data.ok === false ? 'failed' as const : 'ok' as const, summary: text(event.data.summary, 300) };
    let index = -1;
    for (let i = this.steps.length - 1; i >= 0; i--) if (this.steps[i].tool === tool && this.steps[i].status === 'running') { index = i; break; }
    this.steps = index < 0
      ? [...this.steps, { seq: event.seq, tool, label: stepLabel({ tool }), ...finished }].slice(-30)
      : this.steps.map((step, i) => i === index ? { ...step, ...finished } : step);
  }
  // « Dernière action » of the bubble: latest change of the run (replayed history included, it is only displayed).
  private noteAction(event: AgentEvent, kind: string, summary: string) {
    if (event.runId) this.lastAction = { runId: event.runId, text: lastActionLabel(kind, summary, normalizeTarget(event.data.target)) };
  }
  private addChangeset(changeset: AgentChangeset) { if (changeset.id) this.changesets = newestFirst(upsert(this.changesets, changeset, mergeChangeset)); }
  private updateChangeset(id: string) {
    const current = this.changesets.find(changeset => changeset.id === id);
    if (current) this.changesets = upsert(this.changesets, { ...current, status: 'undone' }, mergeChangeset);
  }
  private updateAction(id: string, status: AgentAction['status'], error = '') {
    const current = this.actions.find(action => action.id === id);
    if (current) this.actions = upsert(this.actions, { ...current, status, error }, mergeAction);
  }
  private updateFile(event: AgentEvent, id: string, status: AgentFile['status'], error: string) {
    if (!id) return;
    const current = this.files.find(file => file.id === id);
    // Unknown file (upload response not received yet, or another tab): keep the status, fetch the details.
    if (!current) { if (event.seq <= this.snapshotSeq) return; this.needsSnapshot = true; }
    this.files = upsert(this.files, { id, name: current?.name || '', mimeType: current?.mimeType || '', size: current?.size || 0, status, error }, mergeFile);
  }

  // ---- Writes ---------------------------------------------------------------------------

  private async write<T>(context: AgentErrorContext, request: () => Promise<T>, apply: (value: T) => void,
    options: { pauseReads?: boolean; onError?: (error: unknown) => void } = {}): Promise<boolean> {
    const epoch = this.epoch, token = {}, pausing = this.pausing;
    const current = () => epoch === this.epoch && !this.disposed;
    if (options.pauseReads) { pausing.add(token); this.stopRead(); }
    this.error = ''; this.readError = false; this.changed();
    try {
      const value = await request();
      if (!current()) return false;
      apply(value); this.idlePolls = 0; this.failures = 0; this.stopped = false;
      return true;
    } catch (error) {
      if (current()) { this.error = friendlyError(error, context); options.onError?.(error); }
      return false;
    } finally {
      pausing.delete(token);
      // The panel may have been closed during the request (run not known yet): read once more, poll() ends watching if idle.
      if (current()) { if (!this.opened && this.conversation) this.watching = true; this.pollSoon = true; this.schedule(); this.changed(); }
    }
  }
  // 404 / 409: the local view is behind the server (run already started or finished, action
  // already decided, changeset modified…): read the snapshot again.
  private resyncOnConflict = (error: unknown) => { if ([404, 409].includes((error as any)?.status)) this.needsSnapshot = true; };
  private idFor(signature: string) {
    if (this.retry?.signature !== signature) this.retry = { signature, requestId: this.requestId() };
    return this.retry.requestId;
  }
  // The API titles the conversation from its first message.
  private async ensureConversation(epoch: number, context: AgentContext): Promise<string | null> {
    if (this.conversation) return this.conversation.id;
    if (!this.creating) {
      const body: AgentCreateBody = {};
      if (context.projectId) body.projectId = context.projectId;
      if (context.dpgfId) body.dpgfId = context.dpgfId;
      this.creating = this.api.create(body).then(result => normalizeConversation(result?.conversation || {}));
    }
    const creating = this.creating;
    let created: AgentConversation;
    try { created = await creating; } finally { if (this.creating === creating) this.creating = null; }
    if (epoch !== this.epoch || this.disposed) return null;
    if (!created.id) throw new Error('Réponse invalide');
    if (!this.conversation) {
      this.conversation = created; this.loaded = true;
      this.conversations = [created, ...this.conversations.filter(item => item.id !== created.id)];
      this.drafts.delete('new'); this.drafts.set(created.id, this.draft);
    }
    return this.conversation.id;
  }
  private applyMessage(raw: RawRecord | undefined) {
    const message = raw ? normalizeMessage(raw) : null;
    if (message?.id) this.messages = byDate(upsert(this.messages, message));
  }
  private applyRun(raw: RawRecord | null | undefined) {
    const run = raw ? normalizeRun(raw) : null;
    if (!run?.id) return;
    if (run.id === this.run?.id) this.run = { ...run, lastText: this.run.lastText };
    else { this.run = run; this.steps = []; }
    this.syncQuestionDraft();
    this.runSeq = this.lastSeq;
    this.followRun = { id: run.id, status: run.status };
    if (!isFinalRun(run)) this.liveRun = run.id;
  }
  // Same title rule as the API (first line of the first message), until the next snapshot.
  private touch(content: string) {
    let conversation = this.conversation;
    if (!conversation) return;
    if (!conversation.title) {
      const line = content.split('\n')[0].trim();
      conversation = this.conversation = { ...conversation, title: line.length > AGENT_LIMITS.titleChars ? `${line.slice(0, AGENT_LIMITS.titleChars - 1)}…` : line };
    }
    this.conversations = [conversation, ...this.conversations.filter(item => item.id !== conversation!.id)];
  }

  async send(context: AgentContext = {}) {
    if (!this.canSend) return;
    const epoch = this.epoch, draft = this.draft, content = draft.text.trim(), fileIds = [...draft.fileIds];
    this.sending = true;
    if (this.flush && !(await this.flushPages(epoch))) { if (epoch === this.epoch) { this.sending = false; this.changed(); } return; }
    await this.write('send', async () => {
      const id = await this.ensureConversation(epoch, context);
      if (!id) return null;
      const response = await this.api.send(id, { content, fileIds, requestId: this.idFor(JSON.stringify({ id, content, fileIds })) });
      // Sent: clear this draft even if the user switched conversation meanwhile.
      this.retry = null;
      if (draft.text.trim() === content) draft.text = '';
      draft.fileIds = draft.fileIds.filter(fileId => !fileIds.includes(fileId));
      return response;
    }, response => {
      if (!response) return;
      this.applyMessage(response.message); this.applyRun(response.run); this.touch(content); this.ownRun();
    }, { pauseReads: true, onError: this.resyncOnConflict });
    if (epoch === this.epoch) { this.sending = false; this.changed(); }
  }
  // Answer to the run question: a message on the conversation (the API resumes the waiting run).
  async answer(value: string) {
    const content = (value || '').trim(), id = this.conversation?.id, run = this.run;
    if (!content || content.length > AGENT_LIMITS.messageChars || !id || !run || !this.waiting || this.sending || this.cancelling) return;
    const epoch = this.epoch, draft = this.draft, questionKey = draft.questionKey;
    this.sending = true;
    if (this.flush && !(await this.flushPages(epoch))) { if (epoch === this.epoch) { this.sending = false; this.changed(); } return; }
    await this.write('send', async () => {
      const signature = JSON.stringify({ id, question: run.question?.id || run.id, content });
      const response = await this.api.send(id, { content, fileIds: [], requestId: this.idFor(signature) });
      this.retry = null;
      if (draft.questionKey === questionKey) { draft.answer = ''; draft.answerOptions = []; }
      return response;
    }, response => { this.applyMessage(response?.message); this.applyRun(response?.run); this.touch(content); this.ownRun(); }, { pauseReads: true, onError: this.resyncOnConflict });
    if (epoch === this.epoch) { this.sending = false; this.changed(); }
  }
  async cancel() {
    const run = this.run;
    if (!run || !this.canCancel) return;
    const epoch = this.epoch;
    this.cancelling = true;
    await this.write('cancel', () => this.api.cancel(run.id), response => this.applyRun(response?.run), { pauseReads: true, onError: this.resyncOnConflict });
    if (epoch === this.epoch) { this.cancelling = false; this.changed(); }
  }
  async decide(actionId: string, decision: 'approve' | 'reject') {
    const action = this.actions.find(item => item.id === actionId);
    if (!action || action.status !== 'pending' || this.deciding.has(actionId)) return;
    const epoch = this.epoch;
    this.deciding.add(actionId);
    if (decision === 'approve' && this.flush && !(await this.flushPages(epoch))) { if (epoch === this.epoch) { this.deciding.delete(actionId); this.changed(); } return; }
    await this.write('decide', () => decision === 'approve' ? this.api.approve(actionId) : this.api.reject(actionId), response => {
      const next = normalizeAction(response?.action || {});
      if (next.id === actionId) this.actions = upsert(this.actions, next, mergeAction);
      // An approved deletion is applied as a changeset: show it (and its undo button) right away.
      if (decision === 'approve') this.needsSnapshot = true;
    }, {
      onError: error => {
        const status = (error as any)?.status, code = errorCode(error);
        if (status === 501 || code === 'NOT_IMPLEMENTED') this.updateAction(actionId, 'failed', friendlyError(error, 'decide'));
        else this.resyncOnConflict(error);
      }
    });
    if (epoch === this.epoch) { this.deciding.delete(actionId); this.changed(); }
  }
  async undo(changesetId: string) {
    const changeset = this.changesets.find(item => item.id === changesetId);
    if (!changeset || changeset.status !== 'applied' || this.undoing.has(changesetId)) return;
    const epoch = this.epoch;
    this.undoing.add(changesetId);
    if (this.flush && !(await this.flushPages(epoch))) { if (epoch === this.epoch) { this.undoing.delete(changesetId); this.changed(); } return; }
    await this.write('undo', () => this.api.undo(changesetId), () => this.updateChangeset(changesetId), { onError: this.resyncOnConflict });
    if (epoch === this.epoch) { this.undoing.delete(changesetId); this.changed(); }
  }
  async upload(files: File[], context: AgentContext = {}) {
    if (!files?.length || !this.canUpload) return;
    const problem = uploadProblem(files, this.files.length);
    if (problem) { this.error = problem; this.readError = false; this.changed(); return; }
    const epoch = this.epoch, draft = this.draft;
    this.uploading = true;
    await this.write('upload', async () => {
      const id = await this.ensureConversation(epoch, context);
      return id ? (await this.api.upload(id, files))?.files || [] : null;
    }, uploaded => {
      if (!uploaded) return;
      const added = uploaded.map(normalizeFile).filter(file => file.id);
      for (const file of added) this.files = upsert(this.files, file, mergeFile);
      draft.fileIds = [...new Set([...draft.fileIds, ...added.map(file => file.id)])];
    });
    if (epoch === this.epoch) { this.uploading = false; this.changed(); }
  }
  async transcribe(audio: Blob, context: AgentContext = {}, signal?: AbortSignal): Promise<void> {
    const epoch = this.epoch, draft = this.draft;
    if (signal?.aborted || this.disposed) return;
    const id = await this.ensureConversation(epoch, context);
    if (!id || signal?.aborted || epoch !== this.epoch || this.disposed) return;
    if (!this.api.transcribe) throw new Error('AUDIO_UNSUPPORTED');
    const result = await this.api.transcribe(id, audio, signal);
    if (signal?.aborted || epoch !== this.epoch || this.disposed || this.draft !== draft) return;
    const transcript = typeof result?.text === 'string' ? result.text.trim() : '';
    if (!transcript) throw new Error('AUDIO_EMPTY');
    const combined = draft.text ? `${draft.text.trimEnd()}\n${transcript}` : transcript;
    if (combined.length > AGENT_LIMITS.messageChars) throw new Error('AUDIO_TOO_LONG');
    draft.text = combined; this.error = ''; this.changed();
  }
  removeDraftFile(fileId: string) {
    if (this.sending) return;
    this.draft.fileIds = this.draft.fileIds.filter(id => id !== fileId); this.changed();
  }

  // ---- Internals --------------------------------------------------------------------------

  // Follow mode: the pages save their pending edits first, so the agent works on what the user sees (a failure stops here).
  private async flushPages(epoch: number): Promise<boolean> {
    if (!this.flush) return true;
    this.error = ''; this.changed();
    let saved = false;
    try { saved = (await this.flush()) === true; } catch { saved = false; }
    if (!saved && epoch === this.epoch && !this.disposed) { this.error = FLUSH_ERROR; this.readError = false; }
    return saved && epoch === this.epoch && !this.disposed;
  }
  // sessionStorage {conversationId, runId}: the run this tab follows (written on send / answer / « Suivre ici »).
  private saved() {
    if (this.session !== undefined) return this.session;
    let value: { conversationId: string; runId: string } | null = null;
    try {
      const raw = JSON.parse(this.storage?.getItem(SESSION_KEY) || 'null');
      if (raw && SESSION_ID.test(raw.conversationId) && SESSION_ID.test(raw.runId)) value = { conversationId: raw.conversationId, runId: raw.runId };
    } catch { value = null; }
    return this.session = value;
  }
  private own(conversationId: string, runId: string) {
    this.session = { conversationId, runId };
    try { this.storage?.setItem(SESSION_KEY, JSON.stringify(this.session)); } catch { /* private mode: this page only */ }
  }
  private ownRun() { if (this.conversation && this.run && !isFinalRun(this.run)) this.own(this.conversation.id, this.run.id); }
  private forget() {
    if (this.saved() === null) return;
    this.session = null;
    try { this.storage?.removeItem(SESSION_KEY); } catch { /* ignore */ }
  }

  // Keep a response across polling/navigation, but never carry it into a different question.
  private syncQuestionDraft() {
    const question = this.waiting ? this.run?.question : null;
    const key = question ? JSON.stringify([this.run!.id, question.id, question.text, question.options, question.multiple === true]) : '';
    if (key === this.draft.questionKey) return;
    this.draft.questionKey = key; this.draft.answerOptions = []; this.draft.answer = '';
  }

  private remember() {
    this.drafts.set(this.conversation?.id || 'new', this.draft);
    // The history list keeps the last known run state of the conversation being left.
    const id = this.conversation?.id;
    if (id && this.loaded) this.conversations = this.conversations.map(item => item.id === id ? { ...item, lastRun: normalizeRunState(this.run) } : item);
  }
  private reset() {
    if (this.conversation || this.followRun) { try { this.onReset?.(); } catch { /* ignore */ } }
    this.followFloor = 0; this.followRun = null; this.liveRun = ''; this.lastAction = null; this.activityMemo = null;
    this.epoch++; this.stopRead(); this.pausing = new Set(); this.creating = null;
    this.messages = []; this.run = null; this.steps = []; this.actions = []; this.changesets = []; this.files = [];
    this.lastSeq = 0; this.snapshotSeq = 0; this.runSeq = 0; this.loaded = false; this.needsSnapshot = false; this.pollSoon = false; this.decidedElsewhere = [];
    this.idlePolls = 0; this.failures = 0; this.stopped = false; this.readError = false; this.error = '';
    this.loading = false; this.sending = false; this.uploading = false; this.cancelling = false;
    this.deciding = new Set(); this.undoing = new Set();
  }
}
